import React, { useEffect, useState, useRef } from "react";
import { Button, Checkbox, Form, FormInstance, Input, Radio, Space } from "antd";
import {ServiceInfo, ServiceType} from "../entities/ServiceInfo";
import {PackageTemplate, PackageParam, BuildType} from "../entities/PackageTemplate";
import { PackageTemplateService } from "../http/PackageTemplateService";
import {DEPLOY_ENV, formatDate} from "../utils/config";
import './PackageComponent.css';

interface PackageComponentProps {
    service: ServiceInfo;
    form: FormInstance;
    templateLoaded: (template: PackageTemplate | null) => void;
    filePicked: (file: File) => void;
}

const PackageComponent: React.FC<PackageComponentProps> = ({ service, form, templateLoaded, filePicked }) => {
    const defaultParams = PackageParam.ofDefault();
    const [params, setParams] = useState<PackageParam>(defaultParams);
    const [template, setTemplate] = useState<PackageTemplate | null>(null);
    const [buildType, setBuildType] = useState<BuildType>("LOCAL");
    const packageName = formatDate(new Date()) + "-" + DEPLOY_ENV;
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        form.setFieldsValue({ ...params, packageName, buildType });
    }, [params, packageName, buildType, form]);

    useEffect(() => {
        if (inputRef.current === null) {
            return;
        }
        if (service.type === ServiceType[ServiceType.COCOS]) {
            inputRef.current!.setAttribute("webkitdirectory", "true");
        } else {
            inputRef.current?.removeAttribute("webkitdirectory");
        }
    }, [service.type]);

    useEffect(() => {
        if (template && template.appKey === service.appKey) {
            return;
        }
        PackageTemplateService.getTemplate({ appKey: service.appKey, env: DEPLOY_ENV }).then((response) => {
            setTemplate(response.data);
            templateLoaded(response.data);
            const params = response.data ? response.data!.param : defaultParams;
            setParams(params);
            form.setFieldsValue({ ...params, packageName });
        });
    }, [service.appKey, defaultParams, form, packageName, template, templateLoaded]);

    const onLocalProjectPathChange = (e) => {
        const file = e.target.files[0];
        filePicked(file);
    };

    return (
        <>
            <Form.Item required={true} label="构建方式" name="buildType">
                <Radio.Group onChange={(e) => setBuildType(e.target.value)}>
                    <Radio defaultChecked={true} value="LOCAL">本地上传</Radio>
                    <Radio value="REMOTE">云端构建</Radio>
                </Radio.Group>
            </Form.Item>
            {
                buildType === "LOCAL" && (
                    <Form.Item required={true} label="本地包文件">
                        <input type="file" ref={inputRef} onChange={onLocalProjectPathChange} />
                    </Form.Item>
                )
            }
            {
                buildType === "REMOTE" && (
                    <Form.Item required={true} label="代码分支名称" name="gitBranchName">
                        <Input placeholder="master" />
                    </Form.Item>
                )
            }
            {
                service.type === ServiceType[ServiceType.SPRINGBOOT] && (
                    <Form.Item required={true} label="启动模块" name="workDir">
                        <Input placeholder="SpringBoot的启动文件所在模块" />
                    </Form.Item>
                )
            }
            <Form.Item required={false} label="跳过测试" name="skipTests" valuePropName="checked">
                <Checkbox>跳过测试</Checkbox>
            </Form.Item>

            <Form.Item required={true} label="包名称" name="packageName">
                <Input placeholder="请输入包名称" />
            </Form.Item>

            <Form.Item label="环境变量">
                <Form.List name="variables">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name }) => (
                                <Space className="package-component-space" key={key} align="baseline">
                                    <Form.Item
                                        rules={[{ required: true, message: "请输入环境变量名称" }]}
                                    >
                                        <Input placeholder="名称 (e.g. ENV)" />
                                    </Form.Item>
                                    =
                                    <Form.Item
                                        rules={[{ required: true, message: "请输入环境变量值" }]}
                                    >
                                        <Input placeholder="值 (e.g. java8)" />
                                    </Form.Item>
                                    <Button danger onClick={() => remove(name)}>
                                        删除
                                    </Button>
                                </Space>
                            ))}
                            <Button type="default" onClick={() => add()}>
                                + 新增环境变量
                            </Button>
                        </>
                    )}
                </Form.List>
            </Form.Item>
        </>
    );
};

export default PackageComponent;